'use client';
import { useState } from 'react';
import * as Styled from './styled';
import { IRepositoryEntry, IRepository } from '@/types';
import isEqual from 'lodash-es/isEqual';

import { Stack, Row, Card } from '@/ui/primatives';
import FileVersionSelect from './FileVersionSelect';
import { IRepositoryBranch, IRepositoryBranchesResponse } from '@/lib/repositoryBranchesApi';
import { ITabularFileResponse, IFileVersion } from '@/lib/tabularFileApi';
import useSchema from './useSchema';
import { TCompareDefinition, createCompare } from '@/lib/tabularCompareApi';

import SchemaFields from '@/components/Repository/SchemaSummary/SchemaFields';
import Button from '@/ui/Button';

const SchemaLoader: React.FC<{ repository: IRepository; schema_hash: string }> = ({ repository, schema_hash }) => {
  const { schema, isLoading } = useSchema({ repository, schema_hash });
  return schema && schema.hash === schema_hash && !isLoading ? (
    <SchemaFields schema={schema} closedColumnLimit={20} />
  ) : isLoading ? (
    <div>Loading...</div>
  ) : (
    <div>Unselected</div>
  );
};

const FileCompareSelector: React.FC<{
  // branches: IRepositoryBranch[];
  initialBranch?: IRepositoryBranch;
  initialEntry?: IRepositoryEntry;
  repository: IRepository;
  initialBranchesResponse?: IRepositoryBranchesResponse;
  initialEntriesResponse: ITabularFileResponse;
  initialVersion?: string;
}> = ({ repository, initialBranchesResponse, initialBranch, initialEntry, initialEntriesResponse, initialVersion }) => {
  const [leftVersion, setLeftVersion] = useState<IFileVersion | undefined>(undefined);
  const [rightVersion, setRightVersion] = useState<IFileVersion | undefined>(undefined);

  const hasConflict = leftVersion && rightVersion && isEqual(leftVersion, rightVersion);
  const isValid = leftVersion && rightVersion && !hasConflict;

  const leftPath = leftVersion?.resource?.path
    ? `${leftVersion?.resource.version}/${leftVersion?.resource?.path}`
    : null;
  const rightPath = rightVersion?.resource?.path
    ? `${rightVersion?.resource.version}/${rightVersion?.resource?.path}`
    : null;

  const diffLink = isValid
    ? `/${repository.namespace}/${repository.name}/compare/new/diff?left=${leftPath}&right=${rightPath}`
    : null;

  const isReady = !!leftVersion?.resource && !!rightVersion?.resource;

  const defaultCompare: TCompareDefinition | undefined = isReady
    ? {
        data_frames: {
          left: { path: leftVersion?.resource?.path, version: leftVersion?.resource?.version },
          right: { path: rightVersion?.resource?.path, version: rightVersion?.resource?.version },
        },
        fields: { key: [], compare: [], display: [] },
      }
    : undefined;

  const createDefaultCompare = async () => {
    const { compare } = await createCompare({
      namespace: repository.namespace,
      repositoryName: repository.name,
      payload: defaultCompare as TCompareDefinition,
    });
    if (compare?.compare_number) {
      window.location.href = `/${repository.namespace}/${repository.name}/compare/${compare.compare_number}`;
    }
  };

  return (
    <Styled.FileCompareSelector>
      <Stack>
        <Row gap={40}>
          <Stack>
            <FileVersionSelect
              label="Left"
              repository={repository}
              initialEntry={initialEntry}
              initialBranchesResponse={initialBranchesResponse}
              initialBranch={initialBranch}
              initialEntriesResponse={initialEntriesResponse}
              initialVersion={initialVersion}
              onChange={(v) => setLeftVersion(v)}
            />
            {leftVersion?.schema_hash && <SchemaLoader repository={repository} schema_hash={leftVersion.schema_hash} />}
          </Stack>
          <Stack>
            <FileVersionSelect
              label="Right"
              repository={repository}
              initialBranchesResponse={initialBranchesResponse}
              initialBranch={initialBranch}
              initialEntriesResponse={initialEntriesResponse}
              onChange={(v) => setRightVersion(v)}
            />
            {rightVersion?.schema_hash && (
              <SchemaLoader repository={repository} schema_hash={rightVersion.schema_hash} />
            )}
          </Stack>
        </Row>
        {hasConflict && (
          <Card.Error>
            <div>Both versions are the same</div>
          </Card.Error>
        )}

        <Row gap={20}>
          <Button.Primary disabled={!isReady} onClick={createDefaultCompare}>
            Next
          </Button.Primary>
          {/* <Button.Secondary disabled={!isReady || !diffLink} href={diffLink as string}>
            Advanced Compare
          </Button.Secondary> */}
        </Row>
      </Stack>
    </Styled.FileCompareSelector>
  );
};

export default FileCompareSelector;
